import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

/**
 * Home — minimal per new spec.
 * Only: hero banner slider + a single-line brand intro + primary CTAs.
 * All About/Products/Plan/Mission/Vision/Rewards/Associate content lives on
 * its own dedicated page (see Navbar).
 */

const HERO_SHOTS = [
  {
    hideHeadline: true,
    bgImg: "/product-images/home-banner.jpg",
    fit: "cover",
    bg: "bg-gradient-to-br from-royal via-royal-deep to-black",
    overlay: "bg-gradient-to-t from-royal-deep/40 via-transparent to-transparent",
    primaryLabel: "Shop Wellness",  primaryLink: "/products?category=health",
    secondaryLabel: "Become an IBA", secondaryLink: "/register",
  },
  {
    hideHeadline: true,
    bgImg: "/banners/hero-composite-1.jpeg",
    fit: "cover",
    bg: "bg-gradient-to-r from-[#dfe6d3] via-[#f0eecf] to-[#dfe6d3]",
    overlay: "bg-gradient-to-t from-black/10 via-transparent to-transparent",
    primaryLabel: "Shop Wellness", primaryLink: "/products?category=health",
    secondaryLabel: "Become an IBA", secondaryLink: "/register",
  },
  {
    hideHeadline: true,
    bgImg: "/banners/hero-composite-2.jpeg",
    fit: "cover",
    bg: "bg-gradient-to-r from-[#f6efe1] via-[#e2ecd6] to-[#f6efe1]",
    overlay: "bg-gradient-to-t from-black/10 via-transparent to-transparent",
    primaryLabel: "Shop Madhunashini", primaryLink: "/products?category=health",
    secondaryLabel: "Become an IBA", secondaryLink: "/register",
  },
  {
    hideHeadline: true,
    bgImg: "/product-images/agriculture/family-farm.png",
    fit: "cover",
    bg: "bg-gradient-to-b from-[#4a6d3a] via-[#2f4a24] to-[#1c2f16]",
    overlay: "bg-gradient-to-t from-black/50 via-black/10 to-transparent",
    primaryLabel: "Explore Agriculture", primaryLink: "/products?category=agriculture",
    secondaryLabel: "Become an IBA", secondaryLink: "/register",
  },
  {
    hideHeadline: true,
    bgImg: "/product-images/agriculture/orchard-couple.png",
    fit: "cover",
    bg: "bg-gradient-to-b from-[#5b6d3a] via-[#3f4d24] to-[#1c2f16]",
    overlay: "bg-gradient-to-t from-black/50 via-black/10 to-transparent",
    primaryLabel: "Explore Agriculture", primaryLink: "/products?category=agriculture",
    secondaryLabel: "Become an IBA", secondaryLink: "/register",
  },
];

export default function Home() {
  const [heroIdx, setHeroIdx] = useState(0);
  const [customBanners, setCustomBanners] = useState([]);

  useEffect(() => {
    // Merge superadmin-uploaded banners on TOP of the hardcoded slides.
    // The 5 existing hardcoded shots are untouched — dynamic ones appear first.
    api.get("/banners").then((r) => {
      const list = (r?.data?.items || []).map((b) => ({
        id: b.id,
        hideHeadline: true,
        bgImg: b.image,
        fit: "cover",
        bg: "bg-gradient-to-br from-royal via-royal-deep to-black",
        overlay: "bg-gradient-to-t from-royal-deep/40 via-transparent to-transparent",
        primaryLabel: b.primary_label || "Shop Wellness",
        primaryLink: b.primary_link || (b.category ? `/products?category=${b.category}` : "/products?category=health"),
        secondaryLabel: b.secondary_label || "Become an IBA",
        secondaryLink: b.secondary_link || "/register",
      }));
      if (list.length) setCustomBanners(list);
    }).catch(() => {});
  }, []);

  /** Fire-and-forget click tracker for dynamic banners (superadmin uploads).
   *  Static hardcoded slides have no `id` and are skipped silently. */
  const trackClick = (slide) => {
    if (slide?.id) {
      api.post(`/banners/${slide.id}/click`).catch(() => {});
    }
  };

  const SLIDES = [...customBanners, ...HERO_SHOTS];

  useEffect(() => {
    if (SLIDES.length < 2) return;
    const id = setInterval(() => setHeroIdx((s) => (s + 1) % SLIDES.length), 5000);
    return () => clearInterval(id);
  }, [SLIDES.length]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ============ HERO BANNER ============ */}
      <section className="relative bg-royal overflow-hidden group">
        {SLIDES.map((slide, i) => (
          <div
            key={slide.id || slide.bgImg || `slide-${i}`}
            className={`transition-opacity duration-1000 ${i === heroIdx ? "opacity-100 relative" : "opacity-0 absolute inset-0"}`}
            aria-hidden={i !== heroIdx}
          >
            <div className={`relative ${slide.hideHeadline ? "aspect-[4/5] sm:aspect-[16/9] lg:aspect-[21/9]" : "min-h-[520px] lg:min-h-[620px]"} ${slide.bg}`}>
              <div className="absolute inset-0">
                <img
                  src={slide.bgImg}
                  alt=""
                  loading={i === 0 ? "eager" : "lazy"}
                  className={`w-full h-full ${slide.fit === "contain" ? "object-contain" : "object-cover"} ${slide.hideHeadline && slide.fit !== "contain" ? "object-top bg-royal-deep" : ""}`}
                />
                <div className={`absolute inset-0 ${slide.overlay}`} />
              </div>
              <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 flex items-end ${slide.hideHeadline ? "py-4 sm:py-8 lg:py-10 h-full" : "py-20 lg:py-28 min-h-[520px] lg:min-h-[620px]"}`}>
                <div className="flex flex-row flex-wrap gap-2 sm:gap-3 w-full sm:w-auto">
                  <Link
                    to={slide.primaryLink}
                    onClick={() => trackClick(slide)}
                    data-testid="hero-cta-primary"
                    className="flex-1 sm:flex-initial min-w-[140px] justify-center px-4 py-2.5 sm:px-6 sm:py-3 lg:px-8 lg:py-4 bg-gold text-royal text-sm sm:text-base font-semibold hover:bg-white transition-colors rounded-md inline-flex items-center gap-2 shadow-xl"
                  >
                    {slide.primaryLabel} <ArrowRight size={14} className="sm:size-4" />
                  </Link>
                  <Link
                    to={slide.secondaryLink}
                    onClick={() => trackClick(slide)}
                    data-testid="hero-cta-secondary"
                    className="flex-1 sm:flex-initial min-w-[140px] justify-center px-4 py-2.5 sm:px-6 sm:py-3 lg:px-8 lg:py-4 border-2 border-white/70 text-white hover:bg-white hover:text-royal transition-colors rounded-md inline-flex items-center gap-2 font-medium text-sm sm:text-base backdrop-blur bg-black/25"
                  >
                    {slide.secondaryLabel}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
        {/* Slide indicator dots */}
        {SLIDES.length > 1 && (
          <div
            data-testid="hero-dots"
            className="absolute bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2"
          >
            {SLIDES.map((slide, di) => (
              <button
                key={slide.id || slide.bgImg || `dot-${di}`}
                onClick={() => setHeroIdx(di)}
                aria-label={`Go to slide ${di + 1}`}
                data-testid={`hero-dot-${di}`}
                className={`transition-all rounded-full ${
                  di === heroIdx ? "w-6 h-2 bg-gold" : "w-2 h-2 bg-white/60 hover:bg-white"
                }`}
              />
            ))}
          </div>
        )}
        {SLIDES.length > 1 && (
          <>
            <button
              onClick={() => setHeroIdx((s) => (s - 1 + SLIDES.length) % SLIDES.length)}
              className="hidden sm:grid absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 place-items-center border border-white/40 text-white/70 hover:text-gold hover:border-gold bg-black/20 backdrop-blur rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Previous slide"
            >
              <ArrowLeft size={20} />
            </button>
            <button
              onClick={() => setHeroIdx((s) => (s + 1) % SLIDES.length)}
              className="hidden sm:grid absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 place-items-center border border-white/40 text-white/70 hover:text-gold hover:border-gold bg-black/20 backdrop-blur rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Next slide"
            >
              <ArrowRight size={20} />
            </button>
          </>
        )}
      </section>

      <Footer />
    </div>
  );
}
